import React, { useState, useEffect } from 'react'
import { supabase } from './supabaseClient'
import { useAuth } from './AuthContext'
import { GitBranch, Plus, Save, X, Calendar, User, Shield } from 'lucide-react'

import { C as __C_THEME } from './theme'
const C = {
  ...__C_THEME,
}

const fmt2 = (n) => Number(n || 0).toFixed(2)

export default function Versoes({ inModal = false }) {
  const { isAdmin } = useAuth()
  const [versoes, setVersoes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ versao: '', descricao: '', changelog: '' })
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState({ tipo: '', texto: '' })

  useEffect(() => {
    loadVersoes()
  }, [])

  const loadVersoes = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('versoes')
      .select('*')
      .order('created_at', { ascending: false })
    if (error) {
      setMsg({ tipo: 'erro', texto: 'Erro ao carregar versões: ' + error.message })
    } else {
      setVersoes(data || [])
    }
    setLoading(false)
  }

  const criarVersao = async () => {
    if (!form.versao.trim() || !form.descricao.trim() || !form.changelog.trim()) {
      setMsg({ tipo: 'erro', texto: 'Todos os campos são obrigatórios.' })
      return
    }
    setSaving(true)
    setMsg({ tipo: '', texto: '' })

    const { error } = await supabase.from('versoes').insert({
      versao: form.versao.trim(),
      descricao: form.descricao.trim(),
      changelog: form.changelog.trim(),
    })

    if (error) {
      setMsg({ tipo: 'erro', texto: 'Erro ao criar versão: ' + error.message })
    } else {
      setMsg({ tipo: 'ok', texto: 'Versão criada com sucesso.' })
      setForm({ versao: '', descricao: '', changelog: '' })
      setShowModal(false)
      loadVersoes()
      setTimeout(() => setMsg({ tipo: '', texto: '' }), 3000)
    }
    setSaving(false)
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr)
    return date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div style={{ flex: 1, padding: inModal ? '0' : '20px', overflow: 'auto', backgroundColor: inModal ? C.surface : C.bg }}>
      <div style={{ maxWidth: inModal ? 'none' : '1000px', margin: '0 auto' }}>
        {/* Header — escondido quando renderizado dentro do modal (já tem header próprio) */}
        {!inModal && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div>
              <h1 style={{ fontSize: '22px', fontWeight: '700', color: C.text, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GitBranch size={20} color={C.primary} />
                Histórico de Versões
                <span style={{ fontSize: '10px', padding: '2px 8px', backgroundColor: C.primary, color: '#fff', borderRadius: '10px', fontWeight: '700', letterSpacing: '0.5px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <Shield size={9} /> ADMIN
                </span>
              </h1>
              <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>Acompanhe as atualizações e melhorias do sistema · Atualizado a cada push de código</p>
            </div>
            {isAdmin && (
              <button onClick={() => setShowModal(true)}
                style={{ height: '36px', padding: '0 16px', background: C.primary, border: 'none', borderRadius: '2px', color: '#fff', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Plus size={14} /> Nova Versão
              </button>
            )}
          </div>
        )}

        {/* Em modo modal: subtítulo + botão "Nova Versão" só para admin */}
        {inModal && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
            <p style={{ fontSize: '12px', color: C.textMuted, margin: 0 }}>Acompanhe as atualizações e melhorias do sistema</p>
            {isAdmin && (
              <button onClick={() => setShowModal(true)}
                style={{ height: '32px', padding: '0 14px', background: C.primary, border: 'none', borderRadius: '4px', color: '#fff', fontSize: '12px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <Plus size={13} /> Nova Versão
              </button>
            )}
          </div>
        )}

        {/* Mensagem */}
        {msg.texto && (
          <div style={{ padding: '10px 14px', borderRadius: '2px', marginBottom: '16px', fontSize: '13px', backgroundColor: msg.tipo === 'erro' ? '#fde7e9' : '#dff6dd', border: `1px solid ${msg.tipo === 'erro' ? C.danger : C.success}`, color: msg.tipo === 'erro' ? C.danger : C.success }}>
            {msg.texto}
          </div>
        )}

        {/* Lista de versões */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: C.textMuted }}>
            <p>A carregar versões...</p>
          </div>
        ) : versoes.length === 0 ? (
          <div style={{ backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '40px', textAlign: 'center' }}>
            <GitBranch size={48} color={C.textMuted} style={{ marginBottom: '12px' }} />
            <p style={{ fontSize: '14px', color: C.textMuted, margin: 0 }}>Nenhuma versão registada ainda.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {versoes.map((v) => (
              <div key={v.id} style={{ backgroundColor: C.surface, border: `1px solid ${C.border}`, borderRadius: '4px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: '#eff6fc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <GitBranch size={20} color={C.primary} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: '0 0 2px' }}>v{v.versao}</h3>
                      <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>{v.descricao}</p>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.textMuted }}>
                    <Calendar size={12} />
                    {formatDate(v.created_at)}
                  </div>
                </div>
                <div style={{ backgroundColor: '#f8f8f8', borderRadius: '3px', padding: '12px', border: `1px solid ${C.border}` }}>
                  <p style={{ fontSize: '12px', fontWeight: '700', color: C.text, margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Changelog</p>
                  <div style={{ fontSize: '13px', color: C.text, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                    {v.changelog}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Nova Versão */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 850, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: C.surface, borderRadius: '4px', border: `1px solid ${C.border}`, width: '100%', maxWidth: '500px', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '16px', fontWeight: '700', color: C.text, margin: 0 }}>Nova Versão</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, padding: '4px' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '20px' }}>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: C.text, marginBottom: '6px' }}>Versão *</label>
                <input
                  type="text"
                  value={form.versao}
                  onChange={e => setForm({ ...form, versao: e.target.value })}
                  placeholder="ex: 1.0.0"
                  style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: C.text, marginBottom: '6px' }}>Descrição *</label>
                <input
                  type="text"
                  value={form.descricao}
                  onChange={e => setForm({ ...form, descricao: e.target.value })}
                  placeholder="ex: Atualização do sistema de encomendas"
                  style={{ width: '100%', height: '34px', padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: C.text, marginBottom: '6px' }}>Changelog *</label>
                <textarea
                  value={form.changelog}
                  onChange={e => setForm({ ...form, changelog: e.target.value })}
                  placeholder="- Adicionado campo de código postal&#10;- Corrigido erro ao submeter encomenda&#10;- Atualizado email de encomenda"
                  rows={6}
                  style={{ width: '100%', padding: '8px 10px', border: `1px solid ${C.border}`, borderRadius: '2px', fontSize: '13px', fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical' }}
                />
              </div>
              <button onClick={criarVersao} disabled={saving}
                style={{ width: '100%', height: '36px', backgroundColor: C.primary, color: '#fff', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Save size={13} /> {saving ? 'A criar...' : 'Criar Versão'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
